import { useMemo, useState } from 'react';
import { PencilIcon } from '@heroicons/react/24/outline';
import { PageHeader, Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Badge from '@/components/ui/Badge';
import Modal from '@/components/ui/Modal';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import Pagination from '@/components/ui/Pagination';
import { useAdminVideos } from './useAdminVideos';
import type { AdviceProps } from '@/api/adviceService';

const LETTERS_ONLY_REGEX = /^[a-zA-ZÀ-ÿ\s]+$/;
const SECTIONS = ['Advice', 'Educative'] as const;
const PAGE_SIZE = 20;

const parseCategories = (cat?: string) =>
  cat ? cat.split(',').map((c) => c.trim()).filter(Boolean) : [];

const parseSections = (sec?: string | null) =>
  sec ? sec.split(',').map((s) => s.trim()).filter(Boolean) : [];

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|');

const VideosPage = () => {
  const { advices, allCategories, isLoading, updateTitle, updateCategories, updateSections } =
    useAdminVideos();

  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<AdviceProps | null>(null);

  const [titleInput, setTitleInput] = useState('');
  const [titleError, setTitleError] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [categoryInput, setCategoryInput] = useState('');
  const [categoryError, setCategoryError] = useState('');
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    if (!search.trim()) return advices;
    const q = search.trim().toLowerCase();
    return advices.filter((a) => a.title.toLowerCase().includes(q));
  }, [advices, search]);

  const paged = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page]
  );

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const openEdit = (advice: AdviceProps) => {
    setEditing(advice);
    setTitleInput(advice.title);
    setTitleError('');
    setSelectedCategories(parseCategories(advice.category));
    setCategoryInput('');
    setCategoryError('');
    setSelectedSections(parseSections(advice.section));
  };

  const closeEdit = () => {
    setEditing(null);
    setSaving(false);
  };

  const toggleCategory = (cat: string) =>
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );

  const addCategory = () => {
    const value = categoryInput.trim();
    if (!value) return;
    if (!LETTERS_ONLY_REGEX.test(value)) {
      setCategoryError('Lettres uniquement (pas de chiffres ni caractères spéciaux)');
      return;
    }
    if (!selectedCategories.includes(value)) {
      setSelectedCategories((prev) => [...prev, value]);
    }
    setCategoryInput('');
    setCategoryError('');
  };

  const toggleSection = (sec: string) =>
    setSelectedSections((prev) =>
      prev.includes(sec) ? prev.filter((s) => s !== sec) : [...prev, sec]
    );

  const handleSave = async () => {
    if (!editing) return;
    const trimmedTitle = titleInput.trim();
    if (!trimmedTitle) {
      setTitleError('Le titre ne peut pas être vide');
      return;
    }

    const tasks: Promise<unknown>[] = [];
    if (trimmedTitle !== editing.title) {
      tasks.push(updateTitle({ adviceId: editing.advice_id, title: trimmedTitle }));
    }
    if (!sameSet(selectedCategories, parseCategories(editing.category))) {
      tasks.push(
        updateCategories({ adviceId: editing.advice_id, categories: selectedCategories })
      );
    }
    if (!sameSet(selectedSections, parseSections(editing.section))) {
      tasks.push(updateSections({ adviceId: editing.advice_id, sections: selectedSections }));
    }

    if (tasks.length === 0) {
      closeEdit();
      return;
    }

    setSaving(true);
    try {
      await Promise.all(tasks);
      closeEdit();
    } catch {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader title="Gestion des vidéos" description="Titres, catégories et sections des vidéos" />

      {isLoading && <p className="text-sm text-gray-500">Chargement…</p>}

      {!isLoading && advices.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">Aucune vidéo trouvée</p>
      )}

      {!isLoading && advices.length > 0 && (
        <Card className="p-0">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4 dark:border-gray-800">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              Vidéos ({filtered.length})
            </p>
            <div className="w-full sm:w-64">
              <Input
                placeholder="Rechercher une vidéo…"
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
          </div>
          <Table>
            <THead>
              <tr>
                <TH>Titre</TH>
                <TH>Description</TH>
                <TH>Catégorie</TH>
                <TH>Section</TH>
                <TH className="text-right">Actions</TH>
              </tr>
            </THead>
            <TBody>
              {paged.map((advice) => (
                <TR key={advice.advice_id}>
                  <TD className="max-w-[240px]">
                    <span className="block truncate font-medium text-gray-900 dark:text-gray-100">
                      {advice.title}
                    </span>
                  </TD>
                  <TD className="max-w-[280px]">
                    <span className="block truncate">{advice.description || '-'}</span>
                  </TD>
                  <TD>
                    <div className="flex flex-wrap gap-1">
                      {parseCategories(advice.category).map((cat) => (
                        <Badge key={cat} color="blue">
                          {cat}
                        </Badge>
                      ))}
                      {!advice.category && <span className="text-xs text-gray-400">-</span>}
                    </div>
                  </TD>
                  <TD>
                    <div className="flex flex-wrap gap-1">
                      {parseSections(advice.section).map((sec) => (
                        <Badge key={sec} color="green">
                          {sec}
                        </Badge>
                      ))}
                      {!advice.section && <span className="text-xs text-gray-400">-</span>}
                    </div>
                  </TD>
                  <TD className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEdit(advice)}
                      title="Modifier"
                    >
                      <PencilIcon className="h-4 w-4" />
                      Modifier
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
          {filtered.length === 0 && (
            <p className="px-5 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              Aucune vidéo ne correspond à cette recherche
            </p>
          )}
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={filtered.length}
            onPageChange={setPage}
          />
        </Card>
      )}

      <Modal
        open={!!editing}
        onClose={closeEdit}
        title="Modifier la vidéo"
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={closeEdit} disabled={saving}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-6">
          <Input
            label="Titre"
            placeholder="Titre de la vidéo"
            value={titleInput}
            onChange={(e) => {
              setTitleInput(e.target.value);
              setTitleError('');
            }}
            error={titleError}
          />

          <div className="flex flex-col gap-3">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Catégories</p>
            <div className="flex flex-wrap gap-2">
              {allCategories.map((cat) => {
                const active = selectedCategories.includes(cat);
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => toggleCategory(cat)}
                    className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
                      active
                        ? 'bg-brand-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                  >
                    {cat} {active ? '✕' : '+'}
                  </button>
                );
              })}
              {allCategories.length === 0 && (
                <span className="text-xs text-gray-400">Aucune catégorie existante</span>
              )}
            </div>

            <div className="flex items-end gap-2">
              <Input
                label="Créer une catégorie"
                placeholder="Lettres uniquement"
                value={categoryInput}
                onChange={(e) => {
                  setCategoryInput(e.target.value);
                  setCategoryError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCategory();
                  }
                }}
                error={categoryError}
                className="flex-1"
              />
              <Button variant="secondary" onClick={addCategory} className="mb-[1px]">
                Ajouter
              </Button>
            </div>

            {selectedCategories.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedCategories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => toggleCategory(cat)}
                    className="rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-medium text-brand-600 hover:bg-brand-100 dark:bg-brand-950 dark:text-brand-300"
                  >
                    {cat} ✕
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Sections</p>
            {SECTIONS.map((sec) => (
              <label key={sec} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={selectedSections.includes(sec)}
                  onChange={() => toggleSection(sec)}
                  className="rounded border-gray-300"
                />
                {sec}
              </label>
            ))}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default VideosPage;
